import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { Add, ArrowUpward, Refresh, Stop } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { InkyAvatar } from "./brand/InkyMascot";
import { useChatMemory } from "../context/ChatMemoryContext";
import { useActiveEditor } from "../context/ActiveEditorContext";
import { useAuth } from "../context/EnterpriseAuthContext";
import { useWorkspaceLayout } from "../context/WorkspaceLayoutContext";
import { composeGuidedMessage } from "./editor/sectionHandoff";
import { openDocumentEditor } from "../utils/navigation";
import { editorContextKey } from "../assistant/routeContext";
import { buildPageContext } from "../assistant/surfaceBridge";
import { DEFAULT_CONVERSATION_TITLE, resolveCard, runAssistantTurn } from "../assistant/agentLoop";
import { dropTurn, isTurnActive, newTurnKey, stopTurn, useLatestTurn, useRunningTurn } from "../assistant/assistantTurns";
import { readToolRecord } from "../assistant/conversationContents";
import { resolveActiveContext } from "../assistant/projectGrounding";
import { snapshotEditor } from "../assistant/editorBridge";
import { buildSuggestions } from "../assistant/suggestions";
import { oneLine } from "../assistant/inkyPersona";
import MessageMarkdown, { assistantTextSx } from "../assistant/cards/MessageMarkdown";
import { LiveWorking, TraceStrip } from "../assistant/cards/TurnTrace";
import ResultCard from "../assistant/cards/ResultCards";
import InkyEmptyState from "../assistant/cards/InkyEmptyState";

/**
 * One-click guidance chips for a page handoff (e.g. "Regenerate with Inky"), shown above the
 * composer only while it still holds the handed-off text (the user may add guidance after it).
 * A chip appends its guidance and sends. The chips go away after sending, when the text no
 * longer starts with the handed-off request (cleared or replaced), when the conversation or page
 * changes, or when the panel closes. Reads the handoff from the layout context in this small
 * component so layout changes never re-render the whole thread.
 * @param {{input: string, busy: boolean, conversationId: string|null, onPick: (chip: {label: string, append?: string}) => void}} props - Props.
 * @returns {JSX.Element|null} Chips or null.
 */
function ComposerGuidanceChips({ input, busy, conversationId, onPick }) {
  const { composerChips, clearComposerChips, dock } = useWorkspaceLayout();
  const { pathname } = useLocation();
  /** Where the current chips were handed off: { id, pathname, conversationId, seeded }. */
  const scopeRef = useRef(null);
  const seed = composerChips ? String(composerChips.seed || "").replace(/\s+$/, "") : "";
  const matches = Boolean(composerChips) && Boolean(seed) && input.startsWith(seed);
  useEffect(() => {
    if (!composerChips) {
      scopeRef.current = null;
      return;
    }
    if (!scopeRef.current || scopeRef.current.id !== composerChips.id) {
      scopeRef.current = { id: composerChips.id, pathname, conversationId, seeded: false };
    }
    const scope = scopeRef.current;
    if (!dock.open || pathname !== scope.pathname) {
      clearComposerChips();
      return;
    }
    if (matches) {
      if (!scope.seeded) {
        scope.seeded = true;
        scope.conversationId = conversationId;
      } else if (conversationId !== scope.conversationId) {
        clearComposerChips();
      }
    } else if (scope.seeded) {
      clearComposerChips();
    }
  }, [composerChips, matches, conversationId, pathname, dock.open, clearComposerChips]);
  if (!composerChips || !matches) return null;
  return (
    <Box role="group" aria-label="Quick guidance" sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
      {composerChips.chips.map((chip) => (
        <Button
          key={chip.label}
          size="small"
          disabled={busy}
          onClick={() => {
            clearComposerChips();
            onPick(chip);
          }}
          sx={{
            height: 26,
            px: 1.25,
            minWidth: 0,
            borderRadius: "var(--q-radius-pill, 999px)",
            border: chip.append ? "1px dashed var(--q-ai-border, #C9C1F2)" : "1px solid var(--q-border, #E3E0DA)",
            bgcolor: chip.append ? "var(--q-ai-tint, #F9F8FE)" : "var(--q-surface, #FFFFFF)",
            color: chip.append ? "var(--q-ai-text, #3A28B8)" : "var(--q-text-2, #3D3A35)",
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { bgcolor: chip.append ? "var(--q-ai-tint-strong, #F2EFFD)" : "var(--q-hover, #ECEAE5)" },
          }}
        >
          {chip.label}
        </Button>
      ))}
    </Box>
  );
}

/** Surface name in the shared turn registry. */
const ORIGIN = "dock";
/** Messages rendered at once; older ones load on demand. */
const PAGE_SIZE = 60;
/** Distance from the bottom (px) within which new output keeps the view pinned. */
const STICK_THRESHOLD = 80;
/** Header label when no conversation title belongs to this screen. */
const NEUTRAL_HEADER_TITLE = "Inky";
/** Regenerate counters per conversation (each Regenerate asks for a different answer on purpose). */
const regenerateVariants = new Map();

/**
 * One saved message: user bubble, or Inky's reply with its trace strip and result cards.
 * @param {object} props - Props.
 * @returns {JSX.Element} Row.
 */
const MessageRow = memo(function MessageRow({
  message,
  latestEditCardId,
  tools,
  contextData,
  getEditorRef,
  getMessages,
  updateMessage,
  canRegenerate,
  onRegenerate,
}) {
  const record = message.role === "assistant" ? readToolRecord(message) : null;

  const onResolve = useCallback(
    (cardId, action, values) => {
      const fresh = getMessages(message.conversation_id).find((m) => m.id === message.id) || message;
      return resolveCard({ tools, message: fresh, cardId, action, values, updateMessage });
    },
    [tools, message, getMessages, updateMessage]
  );

  if (message.role === "user") {
    return (
      <Box
        sx={{
          alignSelf: "flex-end",
          maxWidth: "88%",
          bgcolor: "var(--q-accent-soft, #ECE8FC)",
          color: "var(--q-text, inherit)",
          px: 1.5,
          py: 1,
          borderRadius: "var(--q-radius-xl, 12px)",
          borderBottomRightRadius: "var(--q-radius-xs, 4px)",
          contentVisibility: "auto",
          containIntrinsicSize: "auto 48px",
        }}
      >
        <MessageMarkdown role="user" content={message.content} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", maxWidth: "100%", contentVisibility: "auto", containIntrinsicSize: "auto 80px" }}>
      {/* No per-reply avatar: the panel header carries the one Inky mark, and the user's
          messages are right-aligned bubbles, so replies read as Inky's without it. */}
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        {record?.steps?.length > 0 && <TraceStrip steps={record.steps} durationMs={message.tool_call?.durationMs} messageId={message.id} />}
        <MessageMarkdown role="assistant" content={message.content || ""} />
        {record?.cards?.map((card) => (
          <ResultCard
            key={card.id}
            card={card}
            isLatestEdit={card.id === latestEditCardId}
            onResolve={onResolve}
            contextData={contextData}
            getEditorRef={getEditorRef}
          />
        ))}
        {canRegenerate && (
          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<Refresh sx={{ fontSize: 16 }} />}
            onClick={onRegenerate}
            sx={{ mt: 0.5, color: "var(--q-faint, inherit)", fontSize: "0.75rem", minHeight: 0, py: 0.25 }}
          >
            Regenerate
          </Button>
        )}
      </Box>
    </Box>
  );
});

/**
 * Inky, the chat side panel. The agent loop and its live state live outside this component
 * (src/assistant/agentLoop.js + assistantTurns.js), so collapsing the dock, switching to
 * History or closing the mobile drawer never loses a turn in progress.
 * @param {object} props - Component props.
 * @param {string} props.contextSummary - Route/user/account summary from the app shell.
 * @param {Array<object>} props.tools - Inky tool registry (src/assistant/agentTools.js).
 * @param {string} [props.accountId] - Active account id (server resolves the model tier).
 * @param {object} props.contextData - Entity arrays + active ids from the app shell.
 * @param {string | null} [props.contextHint] - Optional subtitle under the title.
 * @param {{conversationId: string, pathname: string} | null} props.titleScope - Screen + conversation allowed to title the header (owned by the dock).
 * @param {(conversationId: string | null) => void} props.onClaimTitleScope - Claims (or clears) the header title for the current screen.
 * @returns {JSX.Element} Chat panel.
 */
function ChatAgentPanel({ contextSummary, tools, accountId, contextData, contextHint = null, titleScope, onClaimTitleScope }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { getEditorRef } = useActiveEditor();
  const {
    currentConversation,
    messages,
    saveMessage,
    updateMessage,
    clearCurrentConversation,
    removeLastMessage,
    getMessages,
    getConversationTitle,
    updateConversationTitle,
    resolveTurnTarget,
    adoptServerConversation,
    ingestServerMessage,
    fetchMessagesAfter,
  } = useChatMemory();

  const conversationId = currentConversation?.id || null;
  const latestTurn = useLatestTurn(ORIGIN);
  const runningHere = useRunningTurn(conversationId);
  const [input, setInput] = useState("");
  /** The keyboard hint under the composer only shows while the composer has focus. */
  const [composerFocused, setComposerFocused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [announcement, setAnnouncement] = useState("");
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const stickRef = useRef(true);

  const isEditorRoute = editorContextKey(pathname, search) != null;
  const active = useMemo(() => resolveActiveContext({ pathname, search, contextData }), [pathname, search, contextData]);

  /** Latest shell context for the loop (read at call time, so a running turn sees fresh data). */
  const contextRef = useRef({});
  contextRef.current = { contextSummary, contextData, pathname, search };

  // The latest dock turn is shown only in the conversation it runs in (or a brand-new thread).
  const turn =
    latestTurn && (latestTurn.conversationId ? latestTurn.conversationId === conversationId : !conversationId)
      ? latestTurn
      : null;
  const running = isTurnActive(turn);
  const busy = running || Boolean(runningHere);
  // Stop was sent to the server, which is ending its current step (the composer stays locked).
  const stopping = Boolean((running && turn?.stopping) || runningHere?.stopping);
  const pendingUserText =
    running && (!turn.userMessageId || !messages.some((m) => m.id === turn.userMessageId)) ? turn.userMessage.content : null;
  const settledProblem = turn && (turn.status === "failed" || turn.status === "stopped") ? turn : null;

  const lastMessage = messages[messages.length - 1] || null;
  const lastUserMessage = useMemo(() => [...messages].reverse().find((m) => m.role === "user") || null, [messages]);
  const latestEditCardId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const cards = readToolRecord(messages[i])?.cards || [];
      const edit = [...cards].reverse().find((card) => card.kind === "doc_edit");
      if (edit) return edit.id;
    }
    return null;
  }, [messages]);

  const editorTitle = `Edit: ${oneLine(active.document?.name || active.template?.name || "Document", 100)}`;

  /**
   * Start a turn in the shared registry.
   * @param {string} text - User message.
   * @param {{existingUserMessage?: object|null, variant?: number}} [options] - Retry/regenerate options.
   * @returns {void}
   */
  const startTurn = useCallback(
    (text, { existingUserMessage = null, variant = 0 } = {}) => {
      stickRef.current = true;
      runAssistantTurn({
        key: newTurnKey(),
        text,
        tools,
        origin: ORIGIN,
        existingUserMessage,
        variant,
        runtime: {
          // Browser loop (fallback) and shared accessors.
          saveMessage,
          getMessages,
          getConversationTitle,
          updateConversationTitle,
          getContext: () => contextRef.current,
          getEditorRef,
          accountId,
          conversationId: existingUserMessage?.conversation_id || conversationId,
          newConversationTitle: isEditorRoute ? editorTitle : undefined,
          // Server agent (inky-agent).
          resolveTarget: resolveTurnTarget,
          getPageContext: () => {
            const ctx = contextRef.current;
            const scope = resolveActiveContext({ pathname: ctx.pathname, search: ctx.search, contextData: ctx.contextData });
            return buildPageContext({ pathname: ctx.pathname, search: ctx.search, active: scope, getEditorRef });
          },
          adoptConversation: adoptServerConversation,
          ingestMessage: ingestServerMessage,
          updateMessage,
          fetchMessagesAfter,
          navigate: (path) => navigate(path),
          openDocument: ({ documentId }) => {
            const owner = (contextRef.current.contextData?.projects || []).find((p) => (p.generatedDocuments || []).some((d) => d.id === documentId));
            openDocumentEditor(navigate, { documentId, projectId: owner?.id });
          },
        },
      });
    },
    [tools, saveMessage, getMessages, getConversationTitle, updateConversationTitle, getEditorRef, accountId, conversationId, isEditorRoute, editorTitle, resolveTurnTarget, adoptServerConversation, ingestServerMessage, updateMessage, fetchMessagesAfter, navigate]
  );

  const send = useCallback(() => {
    // An empty trailing "Guidance:" from a handoff prefill is not sent.
    const text = composeGuidedMessage(input);
    if (!text || busy) return;
    if (settledProblem) dropTurn(settledProblem.key);
    setInput("");
    startTurn(text);
  }, [input, busy, settledProblem, startTurn]);

  /**
   * Send the composer text with a guidance chip's text appended (or as is).
   * @param {{append?: string}} chip - Picked chip.
   * @returns {void}
   */
  const sendWithGuidance = useCallback((chip) => {
    const text = composeGuidedMessage(input, chip?.append);
    if (!text || busy) return;
    if (settledProblem) dropTurn(settledProblem.key);
    setInput("");
    startTurn(text);
  }, [input, busy, settledProblem, startTurn]);

  const stop = useCallback(() => {
    if (isTurnActive(turn)) stopTurn(turn.key);
    else if (runningHere) stopTurn(runningHere.key);
  }, [turn, runningHere]);

  // A refused send (409 busy, a rejected message) puts the text back in the composer.
  const restoreText = settledProblem?.result?.restoreText || null;
  const restoredForRef = useRef(null);
  useEffect(() => {
    if (!restoreText || restoredForRef.current === settledProblem.key) return;
    restoredForRef.current = settledProblem.key;
    setInput((current) => (current.trim() ? current : restoreText));
  }, [restoreText, settledProblem]);

  const retry = useCallback(async () => {
    const info = settledProblem?.result?.retry;
    if (!info || busy) return;
    const savedReplyId = settledProblem.result?.messageId;
    dropTurn(settledProblem.key);
    if (savedReplyId && getMessages(conversationId).slice(-1)[0]?.id === savedReplyId) {
      await removeLastMessage();
    }
    const existing = info.userMessage && getMessages(info.userMessage.conversation_id).some((m) => m.id === info.userMessage.id) ? info.userMessage : null;
    startTurn(info.text, { existingUserMessage: existing, variant: info.variant || 0 });
  }, [settledProblem, busy, getMessages, conversationId, removeLastMessage, startTurn]);

  const regenerate = useCallback(async () => {
    if (busy || !lastMessage || lastMessage.role !== "assistant" || !lastUserMessage) return;
    const variant = (regenerateVariants.get(conversationId) || 0) + 1;
    regenerateVariants.set(conversationId, variant);
    if (!(await removeLastMessage())) return;
    startTurn(lastUserMessage.content, { existingUserMessage: lastUserMessage, variant });
  }, [busy, lastMessage, lastUserMessage, conversationId, removeLastMessage, startTurn]);

  const newChat = useCallback(() => {
    if (settledProblem) dropTurn(settledProblem.key);
    onClaimTitleScope(null);
    clearCurrentConversation();
    setInput("");
    inputRef.current?.focus();
  }, [settledProblem, onClaimTitleScope, clearCurrentConversation]);

  const seedComposer = useCallback((prompt) => {
    setInput(prompt);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(prompt.length, prompt.length);
    });
  }, []);

  const onComposerKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    } else if (event.key === "Escape" && busy) {
      event.preventDefault();
      stop();
    } else if (event.key === "ArrowUp" && !input && lastUserMessage && !busy) {
      event.preventDefault();
      seedComposer(lastUserMessage.content);
    }
  };

  // Header title: claimed by explicit actions on this screen (see ChatAgentDock), by a turn the
  // user starts here, and by the editor's own thread.
  useEffect(() => {
    if (isEditorRoute && conversationId) onClaimTitleScope(conversationId);
  }, [isEditorRoute, conversationId, onClaimTitleScope]);
  useEffect(() => {
    if (turn?.conversationId && turn.conversationId === conversationId) onClaimTitleScope(conversationId);
  }, [turn?.key, turn?.conversationId, conversationId, onClaimTitleScope]);

  const conversationTitle = currentConversation?.title;
  const headerTitle =
    titleScope?.conversationId === conversationId &&
    titleScope?.pathname === pathname &&
    conversationTitle &&
    conversationTitle !== DEFAULT_CONVERSATION_TITLE
      ? conversationTitle
      : NEUTRAL_HEADER_TITLE;

  // A new thread starts pinned to the bottom with the newest page.
  useEffect(() => {
    stickRef.current = true;
    setVisibleCount(PAGE_SIZE);
  }, [conversationId]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD;
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, turn?.live?.text, turn?.live?.steps?.length, turn?.status, pendingUserText, conversationId, lastMessage?.tool_call]);

  // Screen-reader announcements for turn outcomes (progress is announced by InkyWorking).
  const turnStatus = turn?.status || null;
  const turnError = turn?.result?.error || "";
  useEffect(() => {
    if (turnStatus === "done") setAnnouncement("Inky replied.");
    else if (turnStatus === "failed") setAnnouncement(`Inky hit a problem: ${turnError}`);
    else if (turnStatus === "stopped") setAnnouncement("Stopped.");
  }, [turnStatus, turnError]);

  const shownMessages = messages.slice(-visibleCount);
  const hiddenCount = messages.length - shownMessages.length;
  const isEmpty = messages.length === 0 && !pendingUserText && !running && !settledProblem;

  const emptyState = useMemo(() => {
    if (!isEmpty) return null;
    const editor = active.kind === "document" || active.kind === "template" ? snapshotEditor(getEditorRef) : null;
    const suggestions = buildSuggestions(active, { isEmptyDocument: editor?.isEmpty, hasSelection: Boolean(editor?.selection) });
    const contextLine =
      active.kind === "document" && active.document
        ? `I can see "${oneLine(active.document.name, 60)}"${active.project ? ` in ${oneLine(active.project.name, 40)}` : ""}. Ask me to draft, tighten or check it against the requirements.`
        : active.kind === "template" && active.template
          ? `I can see the template "${oneLine(active.template.name, 60)}". Ask me to restructure it or add AI fields.`
          : active.kind === "project" && active.project
            ? `I can see ${oneLine(active.project.name, 60)}. Ask about its meetings and requirements, or generate a document.`
            : active.kind === "session" && active.session
              ? `I can see the meeting "${oneLine(active.session.name, 60)}".`
              : null;
    const firstName = String(authState.user?.username || authState.user?.full_name || "").split(/\s+/)[0];
    return { suggestions, contextLine, firstName };
  }, [isEmpty, active, getEditorRef, authState.user]);

  return (
    <Box
      onKeyDown={(event) => {
        if (event.key === "Escape" && busy) {
          event.preventDefault();
          stop();
        }
      }}
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "var(--q-surface, #FFFFFF)",
        color: "var(--q-text, inherit)",
        overflow: "hidden",
      }}
    >
      <Box aria-live="polite" aria-atomic="true" sx={{ position: "absolute", width: 1, height: 1, m: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
        {announcement}
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderBottom: "1px solid var(--q-border-subtle, #ECE9E4)", flexShrink: 0 }}>
        <InkyAvatar size={30} mood={running ? "thinking" : "idle"} animated={running} decorative />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {headerTitle}
          </Typography>
          <Typography variant="caption" noWrap component="div" sx={{ color: running ? "var(--q-accent, #4F3BD6)" : "var(--q-muted, inherit)", lineHeight: 1.3 }}>
            {stopping ? "Stopping" : turn?.status === "detached" ? "Waiting for the reply" : running ? "Working" : contextHint || "Your PM sidekick"}
          </Typography>
        </Box>
        <Tooltip title="New chat">
          <IconButton size="small" aria-label="New chat" onClick={newChat}>
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Thread */}
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        role="region"
        aria-label="Conversation with Inky"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1.5,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
          scrollbarWidth: "thin",
        }}
      >
        {emptyState && (
          <InkyEmptyState
            greetingName={emptyState.firstName}
            contextLine={emptyState.contextLine}
            suggestions={emptyState.suggestions}
            onPick={seedComposer}
          />
        )}

        {hiddenCount > 0 && (
          <Button size="small" variant="text" color="inherit" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)} sx={{ alignSelf: "center", color: "var(--q-muted, inherit)" }}>
            Show {Math.min(hiddenCount, PAGE_SIZE)} earlier message{Math.min(hiddenCount, PAGE_SIZE) === 1 ? "" : "s"}
          </Button>
        )}

        {shownMessages.map((message) => (
          <MessageRow
            key={message.id || `${message.role}-${message.sequence_number}`}
            message={message}
            latestEditCardId={latestEditCardId}
            tools={tools}
            contextData={contextData}
            getEditorRef={getEditorRef}
            getMessages={getMessages}
            updateMessage={updateMessage}
            canRegenerate={!busy && message === lastMessage && message.role === "assistant" && !readToolRecord(message)?.cards?.length && Boolean(lastUserMessage)}
            onRegenerate={regenerate}
          />
        ))}

        {pendingUserText && (
          <Box sx={{ alignSelf: "flex-end", maxWidth: "88%", bgcolor: "var(--q-accent-soft, #ECE8FC)", px: 1.5, py: 1, borderRadius: "var(--q-radius-xl, 12px)", borderBottomRightRadius: "var(--q-radius-xs, 4px)", opacity: 0.85 }}>
            <MessageMarkdown role="user" content={pendingUserText} />
          </Box>
        )}

        {running && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <Box sx={{ width: 26, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <LiveWorking turn={turn} />
              {turn.live.text && (
                <Typography component="div" sx={{ ...assistantTextSx, whiteSpace: "pre-wrap", mt: 0.75 }}>
                  {turn.live.text}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {settledProblem && (
          <Box
            role="alert"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.25,
              borderRadius: "var(--q-radius-lg, 10px)",
              bgcolor: settledProblem.status === "failed" && !settledProblem.result?.busy ? "var(--q-danger-soft, #FCE8E2)" : "var(--q-subtle, #F1EFEB)",
            }}
          >
            {settledProblem.status === "failed" && <InkyAvatar size={24} mood={settledProblem.result?.busy ? "thinking" : "error"} decorative />}
            <Typography variant="body2" sx={{ flex: 1, fontSize: "0.82rem", color: settledProblem.status === "failed" && !settledProblem.result?.busy ? "var(--q-danger-text, #9C2C14)" : "var(--q-muted, inherit)" }}>
              {settledProblem.status === "failed" ? settledProblem.result?.error : "Stopped."}
            </Typography>
            {settledProblem.result?.retry && (
              <Button size="small" variant="outlined" color="inherit" startIcon={<Refresh sx={{ fontSize: 16 }} />} onClick={retry} disabled={busy}>
                Retry
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Composer */}
      <Box sx={{ flexShrink: 0, px: 1.5, pb: 1.25, pt: 1, borderTop: "1px solid var(--q-border-subtle, #ECE9E4)" }}>
        <ComposerGuidanceChips input={input} busy={busy} conversationId={conversationId} onPick={sendWithGuidance} />
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 0.75,
            border: "1px solid var(--q-border, #E3E0DA)",
            borderRadius: "var(--q-radius-xl, 12px)",
            bgcolor: "var(--q-surface, #FFFFFF)",
            px: 1.25,
            py: 0.75,
            transition: "border-color .15s, box-shadow .15s",
            "&:focus-within": { borderColor: "var(--q-accent, #4F3BD6)", boxShadow: "0 0 0 3px var(--q-focus-halo, rgba(79,59,214,0.12))" },
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={8}
            variant="standard"
            placeholder={isEditorRoute ? "Ask Inky to draft or edit this…" : "Ask Inky anything…"}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={onComposerKeyDown}
            onFocus={() => setComposerFocused(true)}
            onBlur={() => setComposerFocused(false)}
            inputRef={inputRef}
            inputProps={{ "aria-label": "Message Inky" }}
            InputProps={{ disableUnderline: true, sx: { fontSize: "0.875rem", py: 0.5 } }}
          />
          {busy ? (
            <Tooltip title={stopping ? "Stopping after the current step" : "Stop (Esc)"}>
              <span>
                <IconButton
                  size="small"
                  aria-label={stopping ? "Stopping" : "Stop"}
                  onClick={stop}
                  disabled={stopping}
                  sx={{
                    bgcolor: "var(--q-text, #1C1B19)",
                    color: "var(--q-surface, #FFFFFF)",
                    "&:hover": { bgcolor: "var(--q-text-2, #3D3A35)" },
                    "&.Mui-disabled": { bgcolor: "var(--q-border-strong, #D3CFC7)", color: "var(--q-surface, #FFFFFF)" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <Stop sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Send (Enter)">
              <span>
                <IconButton
                  size="small"
                  aria-label="Send"
                  onClick={send}
                  disabled={!input.trim()}
                  sx={{
                    bgcolor: "var(--q-accent, #4F3BD6)",
                    color: "var(--q-on-accent, #FFFFFF)",
                    width: 30,
                    height: 30,
                    "&:hover": { bgcolor: "var(--q-accent-hover, #3A28B8)" },
                    "&.Mui-disabled": { bgcolor: "var(--q-subtle, #F1EFEB)", color: "var(--q-disabled, #9C978F)" },
                  }}
                >
                  <ArrowUpward sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {composerFocused && (
          <Typography variant="caption" component="div" data-testid="composer-hint" sx={{ color: "var(--q-faint, inherit)", mt: 0.5, px: 0.5, fontSize: "0.7rem" }}>
            Enter to send · Shift+Enter for a new line · {busy ? "Esc to stop" : "Up arrow to edit your last message"}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default ChatAgentPanel;
